import React, { useCallback } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, Chip, alpha, useTheme } from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import { 
  SelectControl,
  SliderControl,
} from '../../core/NodeControls';

/**
 * ConcatenateNode represents a layer that concatenates a list of inputs
 */
const ConcatenateNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1, textAlign: 'center' }}>
          Concatenate
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.5 }}>
          <Chip 
            label={`axis: ${data.axis || -1}`}
            size="small"
            sx={{ 
              fontSize: '0.7rem',
              backgroundColor: alpha(theme.palette.grey[700], 0.1),
              color: theme.palette.grey[700],
            }}
          />
        </Box>
        
        {/* Visual representation of concatenation */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            mt: 1,
            gap: 0.5
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box 
              sx={{ 
                width: 15,
                height: 15,
                backgroundColor: alpha(theme.palette.success.main, 0.5),
                borderRadius: 0.5,
              }} 
            />
            <Box 
              sx={{ 
                width: 15,
                height: 15,
                backgroundColor: alpha(theme.palette.info.main, 0.5),
                borderRadius: 0.5,
              }} 
            />
          </Box>
          
          <CallMergeIcon 
            sx={{ 
              fontSize: 16, 
              color: theme.palette.grey[600],
              transform: data.axis === 1 ? 'rotate(90deg)' : 'none' 
            }} 
          />
          
          <Box 
            sx={{ 
              width: 32,
              height: 15,
              backgroundColor: alpha(theme.palette.grey[600], 0.3),
              borderRadius: 0.5,
              border: `1px dashed ${theme.palette.grey[500]}`,
            }} 
          />
        </Box>
      </>
    );
  };
  
  // Controls for the node configuration popover
  const renderControls = () => {
    return (
      <>
        <SelectControl
          value={data.axis || -1}
          onChange={(value) => handleDataChange('axis', parseInt(value))}
          options={[
            { value: -1, label: '-1 (Last Axis)' },
            { value: 0, label: '0 (Batch Axis)' },
            { value: 1, label: '1 (Feature Axis)' },
            { value: 2, label: '2 (Sequence/Height Axis)' },
            { value: 3, label: '3 (Width Axis)' },
          ]}
          label="Concatenation Axis"
          tooltip="The axis along which the inputs will be concatenated"
        />
        
        <SliderControl
          value={data.expected_inputs || 2}
          onChange={(value) => handleDataChange('expected_inputs', value)}
          min={2}
          max={5}
          step={1}
          label="Expected Inputs"
          tooltip="Number of expected input tensors to concatenate"
          marks
        />
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<CallMergeIcon fontSize="small" />}
      nodeType="utility"
      preview={renderPreview()}
      controls={renderControls()}
      multipleInputs={true}  // Can receive multiple inputs
      multipleOutputs={false}
      onDelete={data.onDelete}
    />
  );
};

export default memo(ConcatenateNode); 